/* eslint-disable @typescript-eslint/quotes */
import { Injectable, Inject, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentService } from '../../../environments/environment.service';
import { Content, MiniContent } from '../../domain/content/content.model';
import { MiniContainer } from '../../domain/dynamic-container/dynamic-container.model';

import { BreadcrumbItem, OrganizationData, SearchData, WebpageData, WebsiteData } from './meta.model';
import { TemplateTypeId } from 'src/app/domain/template-ct/template-ct.model';
import { logo } from 'src/app/shared/navigation/navigation.model';


@Injectable({
  providedIn: 'root'
})
export class MetaService {
  constructor(
    private title: Title,
    private metaService: Meta,
    private environmentService: EnvironmentService,
    private tr: TranslateService,
    @Inject(DOCUMENT) private document
  ) { }

  setMeta(content: Content | MiniContent | MiniContainer) {
    this.title.setTitle(content.titolo + ' - DottNet - ' + content.dataCreazione);
    let description: string;
    let image: string;
    if (content?.descrizione && content.descrizione?.length > 0) {
      description = content.descrizione;
    } else {
      description = content.titolo;
    }

    if (content?.pathFoto?.length > 0) {
      // TODO: HERE it enters 4 times
      image = this.environmentService.contentPath + content.pathFoto;
      this.metaService.updateTag({ property: 'og:image', content: image }, "property='og:image'");
      this.metaService.updateTag(
        { property: 'og:image:height', content: '200' },
        "property='og:image:height'"
      );
      this.metaService.updateTag(
        { property: 'og:image:width', content: '200' },
        "property='og:image:width'"
      );
    }
    if (content?.tag?.length > 0) {
      this.metaService.updateTag({ name: 'keywords', content: content.tag }, "name='keywords'");
    }

    this.metaService.updateTag(
      { property: 'og:title', content: content.titolo },
      "property='og:title'"
    );
    this.metaService.updateTag(
      { property: 'og:description', content: description },
      "property='og:description'"
    );
    this.metaService.updateTag(
      { name: 'og:description', content: description },
      "name='og:description'"
    );
    // TODO: check if url needs absolute path
    this.metaService.updateTag(
      { property: 'og:url', content: content.permalink },
      "property='og:url'"
    );

    const url: string = this.environmentService.clientDomain + content.permalink;

    this.createLinkForCanonicalURL(url);

    //set structured data
    const webSiteData: WebsiteData = {
      name: this.environmentService.appName,
      url: this.environmentService.clientDomain,
      description: this.tr.instant('dottnet.meta.website.description'),
      inLanguage: this.environmentService.defaultLanguage,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${this.environmentService.clientDomain}/ricerca/{search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
      publisher: {
        '@type': 'Organization',
        name: this.environmentService.appName,
        url: this.environmentService.clientDomain,
        logo: {
          '@type': 'ImageObject',
          url: `${this.environmentService.clientDomain}${logo}`,
          width: 200,
          height: 50,
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: this.tr.instant('dottnet.meta.contacts.email'),
          contactType: 'customer service'
        },
        sameAs: [
          this.environmentService.fbLink, this.environmentService.twitterLink, this.environmentService.instagramLink, this.environmentService.linkedinLink]
      }
    }
    this.setWebsiteData(webSiteData);

    // const searchData: SearchData = {
    //   name: this.environmentService.appName,
    //   url: this.environmentService.clientDomain,
    //   potentialAction: {
    //     '@type': 'SearchAction',
    //     target: `${this.environmentService.clientDomain}/ricerca/{search_term_string}`,
    //     'query-input': 'required name=search_term_string',
    //   }
    // }

    // this.setSearchAction(searchData);

    const orgData: OrganizationData = {
      name: this.environmentService.appName,
      legalName: this.tr.instant('dottnet.meta.organization.legalName'),
      url: this.environmentService.clientDomain,
      logo: `${this.environmentService.clientDomain}${logo}`,
      description: this.tr.instant('dottnet.meta.organization.description'),
      foundingDate: '2008-01-01',
      address: {
        '@type': 'PostalAddress',
        streetAddress: this.tr.instant('dottnet.meta.organization.streetAddress'),
        addressLocality: this.tr.instant('dottnet.meta.organization.addressLocality'),
        postalCode: this.tr.instant('dottnet.meta.organization.postalCode'),
        addressCountry: this.tr.instant('dottnet.meta.organization.addressCountry')
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: this.tr.instant('dottnet.meta.contacts.telephone'),
        contactType: 'customer service',
        email: this.tr.instant('dottnet.meta.contacts.email'),
        availableLanguage: ['Italian']
      },
      sameAs: [
        this.environmentService.fbLink, this.environmentService.twitterLink, this.environmentService.instagramLink, this.environmentService.linkedinLink
      ],
      department: {
        '@type': 'Organization',
        name: this.tr.instant('dottnet.meta.organization.department.name'),
        url: `${this.environmentService.clientDomain}/redazione`
      },
      knowsAbout: ["Medicina", "Farmacologia", "Salute pubblica", "Telemedicina"]
    }

    this.setOrganizationData(orgData);

    const pageData: WebpageData = {
      name: content.titolo,
      url: url,
      description: description,
      inLanguage: this.environmentService.defaultLanguage,
      isPartOf: {
        '@type': 'WebSite',
        name: this.environmentService.appName,
        url: this.environmentService.clientDomain
      },
      datePublished: content.dataCreazione, //formato AAAA-MM-GG ,
      dateModified: content.dataCreazione, //formato AAAA-MM-GG ,
      publisher: {
        '@type': 'Organization',
        name: this.environmentService.appName,
        url: this.environmentService.clientDomain,
        logo: {
          '@type': 'ImageObject',
          url: `${this.environmentService.clientDomain}${logo}`,
          width: 200,
          height: 50,
        }
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: image,
        width: 1200,
        height: 630,
      }
    }

    this.setWebPageData(pageData);

    // Costruisci l'array di BreadcrumbItem
    const breadcrumbItems: BreadcrumbItem[] = this.buildBreadcrumbItems(content.permalink);
    this.setBreadcrumbList(breadcrumbItems);

    this.setContentData(<Content>content);

  }

  createLinkForCanonicalURL(url: string): void {
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement;
  if (!link) {
    link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    this.document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

  setWebsiteData(websiteData: WebsiteData) {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      ...websiteData,
    };
    this.addJsonLd(data, 'website-jsonld');
  }

  setOrganizationData(orgData: OrganizationData) {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      ...orgData,
    };
    this.addJsonLd(data, 'organization-jsonld');
  }

  setBreadcrumbList(items: Array<BreadcrumbItem>) {
    const itemListElement = items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.item
    }));

    const data = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': itemListElement
    };
    this.addJsonLd(data, 'breadcrumb-jsonld');
  }

  setWebPageData(pageData: WebpageData) {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      ...pageData,
    };
    this.addJsonLd(data, 'webPage-jsonld');
  }

  setSearchAction(searchData: SearchData) {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      ...searchData

    };
    this.addJsonLd(data, 'searchAction-jsonld');
  }

  setContentData(content: Content) {
    let contentData;
    let dataType;
    // Set structured data based on content type
    switch (content.idTemplate) {
      case TemplateTypeId.ARTICOLO || TemplateTypeId.ARTICOLORISERVATO:
        dataType = 'NewsArticle';
        contentData = {
          headline: content.titolo,
          author: {
            '@type': 'Person',
            name: content.autore
          },
          datePublished: content.dataCreazione,
          dateModified: content.dataCreazione,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${this.environmentService.clientDomain}${content.permalink}`
          },
          image: {
            '@type': 'ImageObject',
            url: `${this.environmentService.contentPath}${content.pathFoto}`,
            width: 1200,
            height: 630
          },
          publisher: {
            '@type': 'Organization',
            name: this.environmentService.appName,
            logo: {
              '@type': 'ImageObject',
              url: `${this.environmentService.clientDomain}${logo}`,
              width: 200,
              height: 50
            }
          },
          articleSection: content.descrizioneTopic
        }
        break;
      case TemplateTypeId.VIDEO:
        dataType = 'VideoObject';
        contentData = {
          name: content.titolo,
          description: content.descrizione,
          uploadDate: content.dataCreazione,
          contentUrl: `${this.environmentService.clientDomain}${content.permalink}`,
          thumbnailUrl: `${this.environmentService.contentPath}${content.pathFoto}`,
          publisher: {
            '@type': 'Organization',
            name: this.environmentService.appName,
            logo: {
              '@type': 'ImageObject',
              url: `${this.environmentService.clientDomain}${logo}`,
              width: 200,
              height: 50
            }
          },
          genre: content.descrizioneTopic
        }
        break;
      case TemplateTypeId.DOSSIER:
        dataType = 'Article';
        contentData = {
          headline: content.titolo,
          about: content.descrizione,
          author: {
            '@type': 'Person',
            name: content.autore
          },
          datePublished: content.dataCreazione,
          dateModified: content.dataCreazione,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${this.environmentService.clientDomain}${content.permalink}`
          },
          image: {
            '@type': 'ImageObject',
            url: `${this.environmentService.contentPath}${content.pathFoto}`,
            width: 1200,
            height: 630
          },
          publisher: {
            '@type': 'Organization',
            name: this.environmentService.appName,
            logo: {
              '@type': 'ImageObject',
              url: `${this.environmentService.clientDomain}${logo}`,
              width: 200,
              height: 50
            }
          },
        }
        break;
      case TemplateTypeId.FILE:
        dataType = 'CreativeWork';
        contentData = {
          name: content.titolo,
          description: content.descrizione,
          author: {
            '@type': 'Person',
            name: content.autore
          },
          datePublished: content.dataCreazione,
          fileFormat: 'application/pdf',
          url: `${this.environmentService.clientDomain}${content.permalink}`,
          encodingFormat: 'PDF',
          mainEntityOfPage: `${this.environmentService.contentPath}${content.url}`,
          publisher: {
            '@type': 'Organization',
            name: this.environmentService.appName,
            logo: {
              '@type': 'ImageObject',
              url: `${this.environmentService.clientDomain}${logo}`,
              width: 200,
              height: 50
            }
          }
        }
        break;
      // case TemplateTypeId.FAD:
      //   dataType = 'Course';
      //   contentData = {}
      //   break;
    }

    const data = {
      '@context': 'https://schema.org',
      '@type': dataType,
      ...contentData,
    }

    this.addJsonLd(data, 'content-jsonld');
  }

  private buildBreadcrumbItems(permalink: string): BreadcrumbItem[] {
    const parts = permalink.split('/').filter(part => part);
    const breadcrumbItems: BreadcrumbItem[] = [];


    breadcrumbItems.push({
      name: 'Home',
      item: this.environmentService.clientDomain
    });

    let currentPath = '';

    parts.forEach((part, index) => {
      currentPath += `/${part}`;
      breadcrumbItems.push({
        name: part,
        item: `${this.environmentService.clientDomain}${currentPath}`
      });
    });

    return breadcrumbItems;
  }

  private addJsonLd(data: any, id: string) {
    // Converti i dati in una stringa JSON
    const jsonData = JSON.stringify(data);

    // Cerca un elemento <script> esistente con l'ID specificato
    let script = this.document.getElementById(id) as HTMLScriptElement;

    // Se esiste un elemento <script> con l'ID specificato, sostituiscilo
    if (script) {
      script.textContent = jsonData;
    } else {
      // Altrimenti, crea un nuovo elemento <script>
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.text = jsonData;
      this.document.head.appendChild(script);
    }
  }
}


